import { ActionIcon, Modal, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Button } from "../../../components/shared/Button/Button";
import { useIsOnline, usePostApi, useTheme } from "../../../hooks";
import { useDispatch, useSelector } from "../../../redux/hooks";
import { clearNMEAAmendModalData } from "../../../redux/slices/generatorFeaturesSlice";
import { setNMEAData, setSections } from "../../../redux/slices/generatorSlice";
import { insertValues } from "../../../redux/utils/insertValues";
import { Flex } from "../../../styled";
import { getYachtId } from "../../../utils";
import { useTriggerAutoSave } from "../../hooks/useTriggerAutoSave";
import {
  applyNMEAAmendments,
  deriveNMEAId,
  getClockHourKey,
  getLogbookRowTimeLT,
  getOnlyNMEARows,
  INMEARow,
  normalizeTimeLT,
  syncNMEADataAfterAmend,
} from "../NMEAAutomationModal/NMEAAutomationModal.utils";
import NMEAAmendModalPreview from "./NMEAAmendModalPreview";
import { isLegacyNMEAPreviewLogbook } from "./nmeaPreviewStyling";
import { IRow } from "src/interfaces/Logbooks";

export type previewItem = {
  sectionIndex: number;
  rowIndex: number;
  logbookRow: IRow;
  nmeaRow: INMEARow;
  nmeaDataRow: any;
  sectionName?: string | null;
};

const NMEAAmendModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setValue, getValues } = useFormContext();
  const { lbeidOrPartId, logbookId } = useParams();
  const { post } = usePostApi();
  const triggerAutoSave = useTriggerAutoSave();
  const isOnline = useIsOnline();

  const { NMEAAmendModalData } = useSelector(s => s.generatorFeaturesSlice);
  const { lbentry, sortOrder } = useSelector(s => s.generatorSlice);
  const legacyPreviewEnabled = isLegacyNMEAPreviewLogbook(logbookId);

  const isOpen =
    !legacyPreviewEnabled && Boolean(NMEAAmendModalData?.candidates?.length);

  useEffect(() => {
    if (legacyPreviewEnabled && NMEAAmendModalData) {
      dispatch(clearNMEAAmendModalData());
    }
  }, [dispatch, legacyPreviewEnabled, NMEAAmendModalData]);

  const previewItems: previewItem[] = useMemo(() => {
    if (!NMEAAmendModalData || !isOpen) return [];

    const { candidates } = NMEAAmendModalData;

    return candidates
      .map(candidate => {
        const { sectionIndex, rowIndex, nmeaDataRow } = candidate;
        const section = lbentry.sections[sectionIndex];
        if (!section) return null;

        const logbookRow = section.rows[rowIndex];
        if (!logbookRow) return null;

        const nmeaSections = getOnlyNMEARows(
          [section],
          [nmeaDataRow],
          0,
          undefined,
        );

        const nmeaRow = nmeaSections[0]?.rows[0] ?? null;
        if (!nmeaRow) return null;

        return {
          sectionIndex,
          rowIndex,
          logbookRow,
          nmeaRow,
          nmeaDataRow,
          sectionName: section.title ?? undefined,
        };
      })
      .filter(Boolean) as previewItem[];
  }, [NMEAAmendModalData, lbentry.sections, isOpen]);

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setSelectedRows({});
      return;
    }

    setSelectedRows(
      Object.fromEntries(
        previewItems.map(item => [
          `${item.sectionIndex}-${item.rowIndex}`,
          true,
        ]),
      ),
    );
  }, [isOpen, previewItems]);

  const handleDismiss = () => {
    dispatch(clearNMEAAmendModalData());
  };

  const recordHistory = async (action: string) => {
    if (!isOnline) return;
    await post({
      request: "record-history-action",
      body: {
        params: {
          yachtid: getYachtId(),
          lbid: logbookId,
          lbeid: lbeidOrPartId,
          action,
        },
      },
      shouldRunAfterAutosave: true,
    });
  };

  const handleToggleRow = (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => {
    setSelectedRows(prev => ({
      ...prev,
      [`${sectionIndex}-${rowIndex}`]: checked,
    }));
  };

  const handleToggleSection = (sectionIndex: number, checked: boolean) => {
    setSelectedRows(
      Object.fromEntries(
        previewItems.map(item => [`${sectionIndex}-${item.rowIndex}`, checked]),
      ),
    );
  };

  const handleAccept = async () => {
    if (!NMEAAmendModalData) return;

    await recordHistory("Amended NMEA Data Accepted");

    const filledSections = insertValues(getValues(), lbentry.sections);
    const entryDate: string | null = lbentry?.entrydate ?? null;

    const seenHourKeys = new Set<string>();

    const selectedCandidates = previewItems
      .filter(item => selectedRows[`${item.sectionIndex}-${item.rowIndex}`])
      .map(item => ({
        sectionIndex: item.sectionIndex,
        rowIndex: item.rowIndex,
        rowTimeLT: getLogbookRowTimeLT(
          filledSections[item.sectionIndex]?.rows[item.rowIndex],
        ),
        nmeaDataRow: {
          ...item.nmeaDataRow,
          NMEAId: deriveNMEAId(item.nmeaDataRow),
        },
      }))
      .filter((item): item is typeof item & { rowTimeLT: string } =>
        Boolean(item.rowTimeLT),
      )
      .sort((a, b) => {
        const timeA = normalizeTimeLT(a.rowTimeLT) ?? "";
        const timeB = normalizeTimeLT(b.rowTimeLT) ?? "";
        return timeA.localeCompare(timeB);
      })
      .filter(item => {
        const hourKey = getClockHourKey(entryDate, item.rowTimeLT);
        const sectionHourKey =
          hourKey !== null ? `${item.sectionIndex}-${hourKey}` : null;

        if (!sectionHourKey) return true;
        if (seenHourKeys.has(sectionHourKey)) return false;

        seenHourKeys.add(sectionHourKey);
        return true;
      })
      .map(({ sectionIndex, rowIndex, nmeaDataRow }) => ({
        sectionIndex,
        rowIndex,
        nmeaDataRow,
      }));

    const newSections = applyNMEAAmendments({
      originalSections: filledSections,
      candidates: selectedCandidates,
      setValue,
      sortOrder,
    });

    const updatedNMEAData = syncNMEADataAfterAmend({
      existingNMEAData: lbentry.NMEAData,
      candidates: selectedCandidates,
      sections: newSections,
    });

    dispatch(setSections(newSections));
    dispatch(setNMEAData(updatedNMEAData));
    dispatch(clearNMEAAmendModalData());
    triggerAutoSave();
  };

  return (
    <Modal
      opened={isOpen}
      zIndex={10}
      onClose={handleDismiss}
      centered={false}
      yOffset={50}
      size="80%"
      withCloseButton={false}
      radius={7}
      overlayProps={{ color: "#000", opacity: 0.5 }}
      styles={{
        content: {
          backgroundColor: theme.colors.background[0],
          border: `2px solid ${theme.colors.inputBorder[0]}`,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          maxHeight: "calc(100vh - 100px)",
        },
        body: {
          padding: 18,
          paddingTop: 10,
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 105px)",
        },
      }}>
      <Flex direction="column" gap={10} style={{ flex: 1, minHeight: 0 }}>
        <Flex justify="space-between">
          <Text size="xl" c={theme.colors.text[0]}>
            NMEA Data Now Available
          </Text>
          <ActionIcon onClick={handleDismiss}>
            <IconX />
          </ActionIcon>
        </Flex>

        <Text fz={15}>
          When this entry was created, some NMEA data had not yet arrived from
          the provider. That data is now available.
        </Text>
        <Text fz={15}>
          The rows below were filled manually. Review the comparison and click{" "}
          <strong>Apply NMEA Data</strong> to update them automatically.
        </Text>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overflowX: "auto",
          }}>
          <NMEAAmendModalPreview
            previewItems={previewItems}
            selectedRows={selectedRows}
            onToggleRow={handleToggleRow}
            onToggleSection={handleToggleSection}
          />
        </div>
        <Flex justify="flex-end" mt={10}>
          <Button
            onClick={handleAccept}
            disabled={!Object.values(selectedRows).some((v): boolean => !!v)}>
            Apply NMEA Data
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default NMEAAmendModal;
